import type { EmployeeAgreementFileResult } from "@services/riskAnalysis";

const TERMINAL_STATUSES = new Set<
  EmployeeAgreementFileResult["status"]
>(["processed", "empty", "failed"]);

export type BuildMergedExtractionOptions = {
  /** From SSE `file_extracting` when `pdf` is true; overrides extension guess. */
  pdfHintByFilename?: Readonly<Record<string, boolean>>;
  /** Latest EasyOCR page (1-based) / total for the active PDF row. */
  pageByFilename?: Readonly<Record<string, { page: number; total: number }>>;
};

/** Keep streamed terminal rows when a broken SSE connection has no final payload. */
export function retainExtractionFileResults(
  finalResults: readonly EmployeeAgreementFileResult[] | null | undefined,
  partialResults: readonly EmployeeAgreementFileResult[] | null | undefined,
): EmployeeAgreementFileResult[] | null {
  if (finalResults?.length) {
    return [...finalResults];
  }
  if (partialResults?.length) {
    return [...partialResults];
  }
  return null;
}

/**
 * Merge upload order + current extracting file + streamed `file_result` rows so the UI can show
 * queued / extracting / terminal states for the whole run (including before the first `file_result`).
 */
export function buildMergedExtractionFileResults(
  files: readonly File[],
  extractingFilenames: ReadonlySet<string> | readonly string[] | null,
  partial: readonly EmployeeAgreementFileResult[] | null | undefined,
  opts?: BuildMergedExtractionOptions,
): EmployeeAgreementFileResult[] {
  const extractingSet =
    extractingFilenames instanceof Set
      ? extractingFilenames
      : new Set(extractingFilenames ?? []);
  const partialMap = new Map(
    (partial ?? []).map((r) => [r.filename, r] as const),
  );
  return files.map((f) => {
    const filename = f.name;
    const existing = partialMap.get(filename);
    if (existing && TERMINAL_STATUSES.has(existing.status)) {
      return existing;
    }
    const pdfMayOcr =
      opts?.pdfHintByFilename?.[filename] ??
      filename.toLowerCase().endsWith(".pdf");
    const pageMeta = opts?.pageByFilename?.[filename];
    const hasPageMeta =
      pageMeta != null &&
      Number.isFinite(pageMeta.page) &&
      Number.isFinite(pageMeta.total) &&
      pageMeta.total > 0;
    // Page progress or active extracting set marks this row as in-flight (parallel OCR supported).
    const isActiveExtraction =
      extractingSet.has(filename) || hasPageMeta;
    if (isActiveExtraction) {
      return {
        filename,
        status: "extracting",
        pdf_may_ocr: pdfMayOcr,
        ocr_page: hasPageMeta ? pageMeta.page : null,
        ocr_total_pages: hasPageMeta ? pageMeta.total : null,
      };
    }
    return {
      filename,
      status: "queued",
      pdf_may_ocr: pdfMayOcr,
    };
  });
}

/** Map OCR capacity/service error codes to user-facing Status tab copy. */
export function formatOcrUserError(error: string | null | undefined): string | null {
  if (error == null || error === "") {
    return null;
  }
  const lower = error.toLowerCase();
  if (lower.includes("ocr_storage_full") || lower.includes("storage is full")) {
    return "OCR storage full on server; retry in smaller batches";
  }
  if (
    lower.includes("ocr_service_unavailable") ||
    lower.includes("service unavailable") ||
    lower.includes("503")
  ) {
    return "OCR service busy; retry shortly";
  }
  return error;
}
